import type { CommunityFeedPostFragment } from 'generated/graphql';
import {
  Post,
  PostMediaItem,
  Reaction,
  ReactionType,
  toAuthorId,
  toPostId,
} from './post.types';

export type ServerPost = CommunityFeedPostFragment & {
  author: { email?: string | null };
};

type ServerMedia = ServerPost['media'][number];
type ServerReaction = ServerPost['reactions'][number];

const toMediaItem = (raw: ServerMedia): PostMediaItem | null => {
  const cdn = raw.cdn01 ?? '';
  const isVideo = raw.mimeType?.startsWith('video') ?? false;
  const source = isVideo ? raw.video : raw.image;
  if (!source) return null;

  return {
    type: isVideo ? 'video' : 'image',
    url: `${cdn}${source}`,
    thumbnailUrl: `${cdn}${raw.image ?? source}`,
  };
};

const toReaction = (raw: ServerReaction): Reaction => ({
  type: raw.type,
  count: raw.count,
  isToggled: raw.isToggled,
});

export const toPost = (raw: ServerPost): Post => ({
  id: toPostId(raw.id),
  author: {
    id: toAuthorId(raw.author.id),
    name: raw.author.username,
    avatarUrl: raw.author.profilePicture,
    email: raw.author.email || null,
    isOwn: (raw.author.badges ?? []).includes('own'),
    isCreator: (raw.author.badges ?? []).includes('creator'),
    canReceiveDm: raw.author.hasDirectMessagesAccess === true,
  },
  createdAt: new Date(raw.createdAt),
  content: raw.text,
  media: raw.media
    .map(toMediaItem)
    .filter((item): item is PostMediaItem => Boolean(item)),
  reactions: raw.reactions
    .filter((reaction) => reaction.type !== ReactionType.Comment)
    .map(toReaction),
  commentCount:
    raw.reactions.find((reaction) => reaction.type === ReactionType.Comment)
      ?.count ?? 0,
  pinned: raw.pinned ?? false,
});
